import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useNoSubmit } from "src/apps/AppFlow/pages/hooks";
import { styles } from "src/apps/AppFlow/pages/styles";
import { Checkbox, FormFlowPage, TextInput } from "src/components";
import DropdownSelector from "src/components/DropdownSelector";
import { EmploymentDetailsValue } from "src/types/Application";

import { enums, formatters } from "@fraction/shared";

const { EMPLOYMENT_TYPE_MAP } = formatters.enums;

export interface EmploymentDetailsProps {
  onChange: (value: EmploymentDetailsValue) => void;
  value?: EmploymentDetailsValue;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const EmploymentDetails = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  ...props
}: EmploymentDetailsProps) => {
  const validate = useCallback(
    () => value?.jobTitle !== undefined && value?.employmentType !== undefined && value?.income !== undefined,
    [value]
  );
  const { handleNext, nextDisabled } = useNoSubmit({ onNext, validate });

  const handleChangeJobTitle = useCallback(
    (jobTitle?: string) => {
      onChange({
        ...value,
        jobTitle,
      });
    },
    [onChange, value]
  );

  const handleChangeIncome = useCallback(
    (income?: string) => {
      onChange({
        ...value,
        income,
      });
    },
    [onChange, value]
  );

  const handleChangeEmploymentType = useCallback(
    (employmentType?: enums.EmploymentType) => {
      onChange({
        ...value,
        employmentType,
      });
    },
    [onChange, value]
  );

  const handleChangeStartedLessThan6MonthsAgo = useCallback(
    (startedLessThan6MonthsAgo?: boolean) => {
      onChange({
        ...value,
        startedLessThan6MonthsAgo,
      });
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title="Employment details"
      description="Tell us a bit more about your work. For income, include a rough estimate of any bonuses or commissions you think you'll make this year."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
    >
      <TextInput
        ref={focusRef}
        style={styles.topInput}
        name="jobTitle"
        label="Job title"
        placeholder="Chief Executive Officer"
        value={value?.jobTitle || ""}
        onChange={handleChangeJobTitle}
      />
      <DropdownSelector
        label="Job type"
        value={value?.employmentType}
        onChange={handleChangeEmploymentType}
        options={Object.values(enums.EmploymentType).map((type) => ({
          value: type,
          label: EMPLOYMENT_TYPE_MAP[type],
        }))}
      />
      <TextInput
        style={styles.topInput}
        name="income"
        label="Total annual income"
        placeholder="40,000"
        value={value?.income || ""}
        onChange={handleChangeIncome}
        min={0}
        max={10000000}
        maskOptions={{
          rightAlign: false,
          alias: "currency",
          prefix: "$ ",
          jitMasking: false,
          digits: 0,
        }}
      />
      <Checkbox
        labelStyle={styles.checkboxLabelSmall}
        style={styles.checkbox}
        checked={value?.startedLessThan6MonthsAgo}
        onChange={handleChangeStartedLessThan6MonthsAgo}
        label="Started job less than 6 months ago"
      />
    </FormFlowPage>
  );
};

export default memo(EmploymentDetails);
